/**
 * Centralized configuration for survey question auto-advance functionality
 * This ensures consistent timing and animations across all question types
 */

export const AUTO_ADVANCE_CONFIG = {
  // Animation settings
  ANIMATION_DURATION: 800, // Duration of the blinking animation in milliseconds
  ANIMATION_PATTERN: [0.4, 1, 0.4, 1] as number[], // Opacity pattern for blinking animation
  ANIMATION_EASING: 'easeInOut' as const,
  
  // Progress bar settings
  PROGRESS_BAR_DURATION: 800, // Duration of the progress bar animation (same as animation duration)
  PROGRESS_BAR_COLOR: 'hsl(var(--accent))', // Blue color for the progress bar
  PROGRESS_BAR_OPACITY: 0.2, // Opacity of the progress bar background
  
  // Timing settings
  TOTAL_DELAY: 1000, // Total time before auto-advance (includes animation)
  SHORT_DELAY: 300, // Short delay for simple selections (select, color-selector)
  
  // Calculated values
  get POST_ANIMATION_DELAY() {
    return this.TOTAL_DELAY - this.ANIMATION_DURATION; // 200ms after animation completes
  }
} as const;

/**
 * Animation configuration for Framer Motion
 */
export const AUTO_ADVANCE_ANIMATION = {
  animate: {
    opacity: AUTO_ADVANCE_CONFIG.ANIMATION_PATTERN
  },
  transition: {
    duration: AUTO_ADVANCE_CONFIG.ANIMATION_DURATION / 1000, // Convert to seconds for Framer Motion
    ease: AUTO_ADVANCE_CONFIG.ANIMATION_EASING
  }
} as const;

/**
 * Progress bar animation configuration for Framer Motion
 */
export const PROGRESS_BAR_ANIMATION = {
  initial: { scaleX: 0 },
  animate: { scaleX: 1 },
  transition: {
    duration: AUTO_ADVANCE_CONFIG.PROGRESS_BAR_DURATION / 1000, // Convert to seconds
    ease: 'linear' // Linear for smooth progress
  }
} as const;

/**
 * Hook for managing auto-advance state and animations
 */
export function useAutoAdvance() {
  return {
    config: AUTO_ADVANCE_CONFIG,
    animation: AUTO_ADVANCE_ANIMATION,
    progressBar: PROGRESS_BAR_ANIMATION
  };
} 